/*
 * Copyright (c) 2018-present OOO “INTEGRAL”, gaz-connect.ru. All Rights Reserved.
 * See LICENSE.txt for license information.
 **/
import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import Swiper, { Pagination, Autoplay, SwiperOptions } from 'swiper';
import { select, Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { takeUntil } from 'rxjs/operators';

import { AppMetricaService } from 'src/app/services/app-metrica/app-metrica.service';
import { PromosProvider } from 'src/app/providers/promos/promos';
import { CAR_STATS, INTERCEPTOR_ERRORS } from 'src/app/pages/constants';
import { UtilsService } from 'src/app/services';
import { GazCampusProvider } from 'src/app/providers/gaz-campus/gaz-campus';
import { CarsProvider } from 'src/app/providers/cars/cars';
import { IUserState } from 'src/app/store/state';
import { selectUserIsLoggedIn } from 'src/app/store/selectors';
import { EventCalendarComponent } from 'src/app/components/event-calendar/event-calendar.component';

Swiper.use([Pagination, Autoplay]);

@Component({
  selector: 'app-main',
  templateUrl: './main.page.html',
  styleUrls: ['./main.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MainPage implements OnInit, OnDestroy {
  unsubscribe$: Subject<void>;
  isLoading = true;

  userIsLoggedIn = false;
  promosList: any[] = [];
  materialsList: any[] = [];
  campusEventsList: any[] = [];
  stats: any[] = [];

  swiperOptions: SwiperOptions = {
    pagination: {
      clickable: true,
    },
    rewind: true,
    slidesPerView: 1,
    spaceBetween: 10,
    autoplay: {
      disableOnInteraction: false,
      delay: 5000,
    },
    breakpoints: {
      1200: {
        slidesPerView: 2,
        spaceBetween: 40,
      },
    },
  };

  constructor(
    private appMetrica: AppMetricaService,
    private promosProvider: PromosProvider,
    private gazCampusProvider: GazCampusProvider,
    private carsProvider: CarsProvider,
    private utils: UtilsService,
    private router: Router,
    private store: Store<{ user: IUserState }>,
    private modalCtrl: ModalController,
  ) {
    this.unsubscribe$ = new Subject<void>();
  }

  // переход на страницу с профилем пользователя
  goToProfile() {
    this.router.navigateByUrl('/profile');
  }

  // переход на страницу с уведомлениями
  goToNotifications() {
    this.router.navigateByUrl('/profile/notifications');
  }

  // переход в карточку автомобиля
  goToCar(car) {
    if (car) {
      this.router.navigateByUrl(`/cars/${car.uuid}`);
    }
  }

  // метод открытия модального окна календаря
  async openCalendarModal() {
    const modal = await this.modalCtrl.create({
      component: EventCalendarComponent,
      cssClass: ['calendar-custom-modal', !this.utils.isRatherBigScreen() ? 'mobile-modal' : ''],
    });

    return modal.present();
  }

  // переход к отдельному Промо материалу
  goToPromo(index: number) {
    const promo = this.promosList[index];
    this.router.navigateByUrl(`/promos/${promo.id}`);
  }

  // переход к отдельному Газ-Кампус материалу
  goToMaterial(item) {
    this.router.navigateByUrl(`/gaz-campus/${item.id}`);
  }

  // переход на страницу со всеми материалами
  goToAllMaterials() {
    this.router.navigateByUrl('/gaz-campus/all');
  }

  // открывает страницу с конкретным GAZ Campus событием
  goToEvent(item) {
    this.router.navigateByUrl(`gaz-campus-event/${item.id}`);
  }

  // запрос всех промо
  fetchPromos() {
    return this.promosProvider
      .fetchList()
      .then((data: any[]) => {
        this.promosList = data;
      })
      .catch((err: Error) => {
        if (err.message === INTERCEPTOR_ERRORS.NO_INTERNET_CONNECTION) {
          return;
        }
        const message = this.utils.tr('IDT_PROMOS_LIST_LOADING_ERROR');
        this.utils.presentToast({ message });
      });
  }

  // запрос всех материалов
  fetchMaterials() {
    return this.gazCampusProvider
      .fetchList(4)
      .then((data: any[]) => {
        this.materialsList = data;
      })
      .catch((err: Error) => {
        if (err.message === INTERCEPTOR_ERRORS.NO_INTERNET_CONNECTION) {
          return;
        }
        const message = this.utils.tr('IDT_GAZ_CAMPUS_LIST_LOADING_ERROR');
        this.utils.presentToast({ message });
      });
  }

  // запрос ближайших актуальных двух событий кампуса
  fetchCampusEvents() {
    return this.gazCampusProvider
      .fetchCampusEvents(2, 0, {
        eventDate: { $gte: new Date() },
      })
      .then((data: any[]) => {
        this.campusEventsList = data;
      })
      .catch((err: Error) => {
        if (err.message === INTERCEPTOR_ERRORS.NO_INTERNET_CONNECTION) {
          return;
        }
        const message = this.utils.tr('IDT_GAZ_CAMPUS_EVENTS_LOADING_ERROR');
        this.utils.presentToast({ message });
      });
  }

  makeStatByPriority(car) {
    const errorLamps = car.errorLamps || [];
    const can = car?.telematics?.spMessage?.can;

    if (errorLamps.find((lamp) => lamp.id === 623 && lamp.state)) {
      return CAR_STATS.CRITICAL_ENGINE_FAILURE;
    }

    if (errorLamps.find((lamp) => lamp.id === 624 && lamp.state)) {
      return CAR_STATS.CHECK_ENGINE;
    }

    if (errorLamps.find((lamp) => lamp.id === 1213 && lamp.state)) {
      return CAR_STATS.MIL;
    }

    if (can?.break_fluid_low_level === true) {
      return CAR_STATS.BREAK_FLUID_LOW_LEVEL;
    }

    if (can?.engine_oil_pressure < 80) {
      return CAR_STATS.LOW_OIL_PRESSURE;
    }

    if (can?.coolant_level_low === true) {
      return CAR_STATS.COOLANT_LEVEL_LOW;
    }

    if (can?.generator_malfunction === true) {
      return CAR_STATS.GENERATOR_MALFUNCTION;
    }

    if (can?.remaining_mileage < 1000) {
      return CAR_STATS.REMAINING_MILEAGE;
    }

    return null;
  }

  createCarsStats() {
    this.carsProvider
      .fetchList({
        embedded: {
          telematics: true,
        },
      })
      .then(([cars]) => {
        this.stats = [];

        cars.forEach((car) => {
          const stat = this.makeStatByPriority(car);
          if (stat) {
            this.stats.push({
              car,
              stateNumber: car.parameters.stateNumber,
              stat,
            });
          }
        });
      })
      .catch((err: Error) => {
        if (err.message === INTERCEPTOR_ERRORS.NO_INTERNET_CONNECTION) {
          return;
        }
        const message = this.utils.tr('IDT_CARS_LIST_LOADING_ERROR');
        this.utils.tr(message);
      });
  }

  ionViewWillEnter() {
    this.isLoading = true;

    Promise.all([
      this.fetchPromos(),
      this.fetchMaterials(),
      this.userIsLoggedIn && this.createCarsStats(),
      this.fetchCampusEvents(),
    ]).finally(() => {
      this.isLoading = false;
    });
  }

  ngOnInit() {
    this.appMetrica.reportEvent('Страница "Главная"', { Открыта: new Date() });

    this.store
      .pipe(takeUntil(this.unsubscribe$), select(selectUserIsLoggedIn))
      .subscribe((isLoggedIn) => {
        this.userIsLoggedIn = isLoggedIn;
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
